import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  onAuthStateChanged, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, signInWithPopup,
  signOut, updateProfile,
} from 'firebase/auth';
import {
  doc, setDoc, getDoc, updateDoc, serverTimestamp,
  collection, query, where, orderBy, onSnapshot,
  addDoc,
} from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  const loadProfile = useCallback(async (uid) => {
    const ref  = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    if (snap.exists()) setProfile(snap.data());
  }, []);

  const upsertUserDoc = useCallback(async (fbUser) => {
    const ref  = doc(db, 'users', fbUser.uid);
    const snap = await getDoc(ref);
    const isAdmin = fbUser.uid === process.env.REACT_APP_ADMIN_UID;
    if (!snap.exists()) {
      await setDoc(ref, {
        uid:         fbUser.uid,
        email:       fbUser.email,
        displayName: fbUser.displayName || '',
        photoURL:    fbUser.photoURL    || '',
        bio:         '',
        isAdmin,
        createdAt:   serverTimestamp(),
      });
    } else if (isAdmin && !snap.data().isAdmin) {
      await updateDoc(ref, { isAdmin: true });
    }
    await loadProfile(fbUser.uid);
  }, [loadProfile]);

  // Auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) await upsertUserDoc(u);
      else   setProfile(null);
      setLoading(false);
    });
    return unsub;
  }, [upsertUserDoc]);

  // Real-time global notifications for logged-in users
  useEffect(() => {
    if (!user) { setNotifications([]); return; }
    const q = query(
      collection(db, 'notifications'),
      where('global', '==', true),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(q, snap =>
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [user]);

  // ── Auth actions ────────────────────────────────────────────────
  const signUp = async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName });
    await upsertUserDoc(cred.user);
    return cred;
  };

  const signIn       = (e, p) => signInWithEmailAndPassword(auth, e, p);
  const signInGoogle = ()      => signInWithPopup(auth, googleProvider);
  const logOut       = ()      => signOut(auth);

  const updateUserProfile = async ({ displayName, photoURL, bio }) => {
    if (!user) return;
    const updates = {};
    if (displayName !== undefined) updates.displayName = displayName;
    if (photoURL    !== undefined) updates.photoURL    = photoURL;
    if (bio         !== undefined) updates.bio         = bio;

    if (displayName || photoURL) {
      await updateProfile(user, {
        ...(displayName && { displayName }),
        ...(photoURL    && { photoURL }),
      });
    }
    await updateDoc(doc(db, 'users', user.uid), updates);
    await loadProfile(user.uid);
  };

  // Save watch progress
  const saveProgress = async (movieId, seconds) => {
    if (!user) return;
    await setDoc(doc(db, 'users', user.uid, 'progress', String(movieId)), {
      movieId, seconds, updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const getProgress = async (movieId) => {
    if (!user) return 0;
    const snap = await getDoc(doc(db, 'users', user.uid, 'progress', String(movieId)));
    return snap.exists() ? snap.data().seconds : 0;
  };

  // Movie request
  const requestMovie = async (title, note = '') => {
    if (!user) throw new Error('Login required');
    await addDoc(collection(db, 'movieRequests'), {
      uid:         user.uid,
      displayName: user.displayName || user.email,
      title, note,
      status:      'pending',
      createdAt:   serverTimestamp(),
    });
  };

  const isAdmin = profile?.isAdmin || user?.uid === process.env.REACT_APP_ADMIN_UID;

  return (
    <AuthContext.Provider value={{
      user, profile, loading, isAdmin, notifications,
      signUp, signIn, signInGoogle, logOut,
      updateUserProfile, saveProgress, getProgress, requestMovie,
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
